// The server's own emoji and stickers in the web app: a context the Server page fills from the room's snapshot, and
// the pieces that draw them (in messages, reactions, the pickers). Pictures come from /api/servers/:id/emoji/:id
// (members only, cached for good since ids never change). A token whose emoji is gone shows as ":name:".
import { createContext, useContext, useMemo, useState } from "react";
import { emojiToken, emojiUrl, parseEmojiToken, stickerUrl, type CustomEmoji, type Sticker } from "@bloom/shared";
import { useUsedEmoji } from "./emoji.ts";
import "./components/emoji-admin.css";
import { imgProps } from "./imageMemory.ts";

export interface ServerEmoji {
  serverId: string;
  emojis: CustomEmoji[];
  stickers: Sticker[];
  byId: Map<string, CustomEmoji>;
  /** The lists are known (from the room or the device cache), so an id missing from them really is deleted. */
  known: boolean;
}

const EMPTY: ServerEmoji = { serverId: "", emojis: [], stickers: [], byId: new Map(), known: false };
const Ctx = createContext<ServerEmoji>(EMPTY);

export function ServerEmojiProvider({ serverId, emojis, stickers, known, children }: { serverId: string; emojis: CustomEmoji[]; stickers: Sticker[]; known: boolean; children: React.ReactNode }) {
  const value = useMemo(() => ({ serverId, emojis, stickers, byId: new Map(emojis.map((e) => [e.id, e])), known }), [serverId, emojis, stickers, known]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useServerEmoji = () => useContext(Ctx);

/**
 * The emoji I used most recently (or most often) on this device that can be shown here: Unicode, plus this server's
 * own emoji that still exist, as their current token (a renamed one carries its new name). Another server's emoji
 * never show up.
 */
export function useUsedHere(kind: "recent" | "frequent", n: number): string[] {
  const { byId } = useServerEmoji();
  const used = useUsedEmoji(kind, n, (u) => {
    const t = parseEmojiToken(u);
    return !t || byId.has(t.id);
  }, byId);
  return useMemo(() => used.map((u) => {
    const t = parseEmojiToken(u);
    const e = t && byId.get(t.id);
    return e ? emojiToken(e) : u;
  }), [used, byId]);
}

/** One custom emoji, at text size (CSS makes it bigger in an emoji-only message). Gone: its name as text. */
export function CustomEmojiView({ id, name, size, className = "cemoji" }: { id: string; name: string; size?: number; className?: string }) {
  const { serverId, byId, known } = useServerEmoji();
  const [broken, setBroken] = useState(false);
  const current = byId.get(id);
  const label = `:${current?.name ?? name}:`;
  if (!serverId || broken || (known && !current)) return <span className="cemoji-gone" title="This emoji was deleted">{label}</span>;
  return (
    <img
      className={className}
      src={emojiUrl(serverId, id)}
      alt={label}
      title={label}
      draggable={false}
      {...imgProps(emojiUrl(serverId, id))}
      width={size}
      height={size}
      onError={() => setBroken(true)}
    />
  );
}

/** A reaction's emoji: Unicode as is, a server emoji as its picture. */
export function ReactionGlyph({ emoji }: { emoji: string }) {
  const t = parseEmojiToken(emoji);
  return t ? <CustomEmojiView id={t.id} name={t.name} className="cemoji cemoji-reaction" /> : <>{emoji}</>;
}

/** What a reaction is called in labels: ":name:" for a server emoji. */
export function reactionLabel(emoji: string, byId: Map<string, CustomEmoji>): string {
  const t = parseEmojiToken(emoji);
  return t ? `:${byId.get(t.id)?.name ?? t.name}:` : emoji;
}

/** A sticker in a message. Fixed size, so the list never shifts while it loads; a deleted one shows its name. */
export function StickerView({ id, name }: { id: string; name: string }) {
  const { serverId, stickers, known } = useServerEmoji();
  const [broken, setBroken] = useState(false);
  const gone = broken || (known && !stickers.some((s) => s.id === id));
  if (gone || !serverId) {
    return (
      <div className="sticker sticker-gone" role="img" aria-label={`Sticker: ${name} (deleted)`}>
        <span>{name}</span>
        <span className="muted">Sticker deleted</span>
      </div>
    );
  }
  return <img className="sticker" src={stickerUrl(serverId, id)} alt={`Sticker: ${name}`} title={name} width={160} height={160} {...imgProps(stickerUrl(serverId, id))} draggable={false} onError={() => setBroken(true)} />;
}

/** Server emoji matching what's typed after ":" (name starts with it first, then contains it). */
export function searchCustom(list: CustomEmoji[], query: string, limit = 8): CustomEmoji[] {
  const q = query.toLowerCase().replace(/^:|:$/g, "");
  if (!q) return list.slice(0, limit);
  const starts = list.filter((e) => e.name.toLowerCase().startsWith(q));
  const contains = list.filter((e) => !starts.includes(e) && e.name.toLowerCase().includes(q));
  return [...starts, ...contains].slice(0, limit);
}
